/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { ClinicProfileSettingsResponse } from "../../../../models/clinic/profile/ClinicProfileSettingsResponse";
import { useFieldArray, useForm } from "react-hook-form";
import { strings } from "../../../../common/Strings/Strings";
import ClinicProfileApi from "../../../../api/ClinicProfileApi";
import { canManageClinic, getGeneralError } from "../../../../util/helperFunctions";
import AlertBox, { AlertType } from "../../../../components/AlertBox";
import Button from "../../../../components/Button";
import { defaultCalendarEndTime, defaultCalendarStartTime } from "../../../Calendar/calendarConfig";
import { DayOfWeek } from "../../../../models/shift/DayOfWeek";
import { OpeningHoursItemRequest } from "../../../../models/clinic/profile/OpeningHoursItemRequest";
import CheckBox from "../../../../components/ReactHookFormFields/General/CheckBox";
import Field from "../../../../components/ReactHookFormFields/General/Field";
import { ClinicResponse } from "../../../../models/clinic/ClinicResponse";

interface Props {
  clinic: ClinicResponse;
  settings: ClinicProfileSettingsResponse;
  setSettings: (settings: ClinicProfileSettingsResponse) => void;
}

interface OpeningHourItem {
  day: string;
  closed: boolean;
  startTime?: string;
  endTime?: string;
}

interface OpeningHoursForm {
  openingHours: OpeningHourItem[];
}

const defaultOpeningHours = (settings: ClinicProfileSettingsResponse) => {
  const hours: OpeningHourItem[] = settings.openingHours.map((item) => ({
    day: item.day,
    closed: !item.open,
    startTime: item.open?.startTime || settings?.calendarDayStartTime || defaultCalendarStartTime,
    endTime: item.open?.endTime || settings?.calendarDayEndTime || defaultCalendarEndTime,
  }));
  Object.keys(DayOfWeek).forEach((day) => {
    if (!hours.find((i) => i.day == day)) {
      hours.push({ day: day, closed: true, startTime: undefined, endTime: undefined });
    }
  });
  return hours;
};

const defaultFormValues = (settings: ClinicProfileSettingsResponse): OpeningHoursForm => ({
  openingHours: defaultOpeningHours(settings),
});

const OpeningHours: React.FC<Props> = (props: Props) => {
  const { clinic, settings, setSettings } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, errors },
  } = useForm<OpeningHoursForm>({
    mode: "onSubmit",
    defaultValues: defaultFormValues(settings),
  });

  const openingHoursArray = useFieldArray({ control, name: "openingHours" });
  const openingHours = watch("openingHours");

  useEffect(() => {
    reset(defaultFormValues(settings));
  }, [settings]);

  const saveSettings = async (data: OpeningHoursForm) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const openingHours: OpeningHoursItemRequest[] = data.openingHours.map((item) => ({
        day: item.day,
        open:
          item.closed || !item.startTime || !item.endTime
            ? undefined
            : { startTime: item.startTime, endTime: item.endTime },
      }));

      const request = { openingHours };
      const response = await ClinicProfileApi.updateOpeningHours(clinic.id, request);
      setSettings(response.data);
      setSuccess(true);
    } catch (e) {
      setError(await getGeneralError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(saveSettings)}>
      <div className="tw-card divide-y divide-gray-200 dark:divide-gray-700">
        <div className="p-4 flex items-center">
          <p className="uppercase">{strings.clinicProfileOpeningHours}</p>
        </div>
        <div className="p-4 space-y-4">
          {openingHoursArray.fields.map((item, index) => (
            <div className="mb-4">
              <div className="flex align-middle space-x-2">
                <div className="flex-1">
                  <label>{strings[item.day]}</label>
                  <CheckBox
                    name={`openingHours.${index}.closed`}
                    register={register}
                    label={strings.clinicClosed}
                    className="mt-4"
                    readOnly={!canManageClinic(clinic)}
                  />
                </div>
                <div className="flex-1">
                  <Field
                    type="time"
                    className="mb-0"
                    register={register}
                    name={`openingHours.${index}.startTime`}
                    label={strings.clinicOpensAt}
                    error={errors.openingHours && errors.openingHours[index]?.startTime}
                    readOnly={(openingHours && openingHours[index]?.closed) || !canManageClinic(clinic)}
                  />
                </div>
                <div className="flex-1">
                  <Field
                    type="time"
                    className="mb-0"
                    register={register}
                    name={`openingHours.${index}.endTime`}
                    label={strings.clinicClosesAt}
                    error={errors.openingHours && errors.openingHours[index]?.endTime}
                    readOnly={(openingHours && openingHours[index]?.closed) || !canManageClinic(clinic)}
                  />
                </div>
              </div>
            </div>
          ))}
          <AlertBox message={error} />
          <AlertBox closeAble={false} hidden={!success} message={strings.savedSuccessfully} type={AlertType.SUCCESS} />
          <div className="text-right">
            <Button
              disabled={loading || !isDirty}
              loading={loading}
              type="submit"
              fullWidth={false}
              hidden={!canManageClinic(clinic)}
            >
              {strings.save}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default OpeningHours;
